// ExpenseForm.tsx - COMPLETE CLEAN VERSION WITH USER-FRIENDLY SUBMIT

import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Select, 
  MenuItem, 
  Button, 
  SelectChangeEvent,
  CircularProgress,
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Backdrop,
  Card,
  CardContent,
  LinearProgress,
  Snackbar,
  Slide,
  Fade
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { 
  Camera, 
  Upload, 
  X as ClearIcon, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Send,
  Calculator
} from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { DropdownOption } from '../constants/dropdownOptions';
import ShowRunnrCalc from './ShowRunnrCalc';
import heic2any from "heic2any";
import PDFPreview from '../components/PDFPreview';
  
dayjs.locale('en-gb');

interface Staff {
  _id: string;
  name: string;
  employmentStatus: string;
}

interface ExpenseFormData {
  staffId: string;
  amount: string;
  description: string;
  location: string;
  date: Dayjs | null;
  paymentMethod: string;
  activityId: string;
  imcCode: string;
  imcCodeMondayId: string;
  vat: boolean;
  vatAmount: string;
  businessName: string;
}

interface SubmitState {
  isSubmitting: boolean;
  stage: 'idle' | 'validating' | 'uploading' | 'submitting' | 'success' | 'error';
  progress: number;
  message: string;
  error?: string;
}

const ExpenseForm: React.FC = () => {
  const [formData, setFormData] = useState<ExpenseFormData>({
    staffId: '',
    amount: '',
    description: '',
    location: '',
    date: dayjs(),
    paymentMethod: '',
    activityId: '',
    imcCode: '',
    imcCodeMondayId: '',
    vat: false,
    vatAmount: '',
    businessName: '',
  });

  const [receiptPhoto, setReceiptPhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityOptions, setActivityOptions] = useState<DropdownOption[]>([]);
  const [imcCodes, setImcCodes] = useState<{ value: string; label: string; mondayId: string }[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<DropdownOption[]>([]);
  const [businessNames, setBusinessNames] = useState<{ value: string; label: string }[]>([]);
  const [submitState, setSubmitState] = useState<SubmitState>({
    isSubmitting: false,
    stage: 'idle',
    progress: 0,
    message: '',
    error: undefined
  });
  const [isCalcOpen, setIsCalcOpen] = useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const updateSubmitState = (updates: Partial<SubmitState>) => {
    setSubmitState(prev => ({ ...prev, ...updates }));
  };
  const resetSubmitState = () => {
    setSubmitState({
      isSubmitting: false,
      stage: 'idle',
      progress: 0,
      message: '',
      error: undefined
    });
  };

  useEffect(() => {
    if (submitState.isSubmitting) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [submitState.isSubmitting]);

  useEffect(() => {
    console.log('Current API URL:', process.env.REACT_APP_API_URL);
    console.log('Auth Token:', localStorage.getItem('token'));
  }, []);

useEffect(() => {
  const fetchStaff = async () => {
    try {
      const response = await axiosInstance.get<Staff[]>(`/api/personnel`);
      const employees = response.data.filter(person => person.employmentStatus === 'employee');
      setStaff(employees);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError('Failed to fetch staff members. Please try again later.');
      setLoading(false);
    }
  };
  fetchStaff();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prevState => ({
            ...prevState,
            location: `${position.coords.latitude}, ${position.coords.longitude}`
          }));
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  }, []);

useEffect(() => {
  const fetchActivities = async () => {
    try {
      const response = await axiosInstance.get('/api/monday/activities');
      setActivityOptions(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchIMCCodes = async () => {
    try {
      const response = await axiosInstance.get('/api/monday/imc');
      setImcCodes(response.data);
      console.log(`Loaded ${response.data.length} IMC codes from Monday.com`);
    } catch (error) {
      console.error('Error fetching IMC codes:', error);
    }
  };

  const fetchPaymentMethods = async () => {
  try {
    const response = await axiosInstance.get('/api/monday/payment-methods');
    setPaymentMethods(response.data);
    console.log(`Loaded ${response.data.length} payment methods from Monday.com`);
  } catch (error) {
    console.error('Error fetching payment methods:', error);
  }
};

const fetchBusinessNames = async () => {
  try {
    const response = await axiosInstance.get('/api/expenses/business-names');
    setBusinessNames(response.data);
  } catch (error) {
    console.error('Error fetching business names:', error);
  }
};

  fetchActivities();
  fetchIMCCodes();
  fetchPaymentMethods();
  fetchBusinessNames();
}, []);

const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

  };

  const handleDateChange = (date: Dayjs | null) => {
    setFormData(prevState => ({ ...prevState, date }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      if (file.type === 'application/pdf') {
        setReceiptPhoto(file);
      } else if (file.type === 'image/heic' || file.name.endsWith('.heic')) {
        try {
          const convertedBlob = await heic2any({ 
            blob: file, 
            toType: "image/jpeg", 
            quality: 0.85 
          });
          
          const convertedFile = new File([convertedBlob as BlobPart], file.name.replace(/\.heic$/i, '.jpg'), { type: "image/jpeg" });
          setReceiptPhoto(convertedFile);
        } catch (error) {
          console.error('Error converting HEIC:', error);
        }
      } else if (file.type.startsWith('image/')) {
        setReceiptPhoto(file);
      }
    }
  };

  useEffect(() => {
    if (receiptPhoto) {
      const url = URL.createObjectURL(receiptPhoto);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [receiptPhoto]);

  const handleCameraCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.capture = 'environment';
    input.onchange = (e) => handleFileChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
    input.click();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    updateSubmitState({
      isSubmitting: true,
      stage: 'validating',
      progress: 10,
      message: 'Validating form...'
    });

    const token = localStorage.getItem('token');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 300)); // Brief pause so user sees validation

      if (!formData.staffId || !formData.amount || !formData.date) {
        updateSubmitState({
          isSubmitting: false,
          stage: 'error',
          progress: 0,
          message: '',
          error: 'Please fill in all required fields'
        });
        return;
      }

      updateSubmitState({
        stage: 'uploading',
        progress: 30,
        message: receiptPhoto ? 'Preparing file upload...' : 'Preparing submission...'
      });

      const expenseData = new FormData();
      
      Object.entries(formData).forEach(([key, value]) => {
  if (key === 'date' && value) {
    const dateStr = (value as Dayjs).format('YYYY-MM-DD');
    expenseData.append(key, dateStr);
  } else if (key === 'amount') {
    const numAmount = parseFloat(value as string);
    if (!isNaN(numAmount)) {
      expenseData.append(key, numAmount.toString());
    }
  } else {
    expenseData.append(key, value?.toString() || '');
  }
});


      if (receiptPhoto) {
        updateSubmitState({
          stage: 'uploading',
          progress: 50,
          message: `Uploading receipt: ${receiptPhoto.name}`
        });
        
        expenseData.append('receiptPhoto', receiptPhoto);
        expenseData.append('receiptPhotoName', receiptPhoto.name);
      }

      updateSubmitState({
        stage: 'submitting',
        progress: 70,
        message: 'Submitting expense to server...'
      });

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',

        },
        withCredentials: true,
        onUploadProgress: (progressEvent: any) => {
          const uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const adjustedProgress = 50 + (uploadProgress * 0.3); // Maps to 50-80% of total progress
          updateSubmitState({
            progress: adjustedProgress,
            message: receiptPhoto ? `Uploading receipt: ${uploadProgress}%` : 'Submitting...'
          });
        }
      };

  const response = await axiosInstance.post(
  '/api/expenses',
  expenseData,
  config
);

      updateSubmitState({
        stage: 'success',
        progress: 100,
        message: 'Expense submitted successfully!'
      });

      if (response.status === 201 && response.data) {
        setTimeout(() => {
          setFormData({
            staffId: '',
            amount: '',
            description: '',
            location: '',
            date: dayjs(),
            paymentMethod: '',
            activityId: '',
            imcCode: '',
            imcCodeMondayId: '',
            vat: false,
            vatAmount: '',
            businessName: '',
          });
          setReceiptPhoto(null);
          setPreviewUrl(null);
          
          setShowSuccessSnackbar(true);
          resetSubmitState();
        }, 1500);
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error: any) {
      console.error('Error submitting expense:', error);
      
      const errorMessage = error.response?.data?.message || 
         'Failed to submit expense. Please check all fields and try again.';
      
      updateSubmitState({
        isSubmitting: false,
        stage: 'error',
        progress: 0,
        message: '',
        error: errorMessage
      });
    }
  };

  const handleCalcSubmit = (value: number) => {
    setFormData(prevState => ({
      ...prevState,
      amount: value.toString()
    }));
    setIsCalcOpen(false);
  };

  const getSubmitButtonConfig = () => {
    switch (submitState.stage) {
      case 'validating':
        return {
          text: 'Validating...',
          icon: <Clock size={20} />,
          disabled: true,
          color: 'primary' as const
        };
      case 'uploading':
        return {
          text: receiptPhoto ? 'Uploading Receipt...' : 'Preparing...',
          icon: <CircularProgress size={20} />,
          disabled: true,
          color: 'primary' as const
        };
      case 'submitting':
        return {
          text: 'Submitting...',
          icon: <CircularProgress size={20} />,
          disabled: true,
          color: 'primary' as const
        };
      case 'success':
        return {
          text: 'Success!',
          icon: <CheckCircle size={20} />,
          disabled: true,
          color: 'success' as const
        };
      case 'error':
        return {
          text: 'Try Again',
          icon: <AlertCircle size={20} />,
          disabled: false,
          color: 'error' as const
        };
      default:
        return {
          text: 'Submit Expense',
          icon: <Send size={20} />,
          disabled: false,
          color: 'primary' as const
        };
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
  );

  const buttonConfig = getSubmitButtonConfig();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
        open={submitState.isSubmitting}
      >
        <Card sx={{ p: 4, maxWidth: 400, width: '90%', textAlign: 'center' }}>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              {submitState.stage === 'validating' && <Clock size={48} />}
              {submitState.stage === 'uploading' && <Upload size={48} />}
              {submitState.stage === 'submitting' && <Send size={48} />}
              {submitState.stage === 'success' && <CheckCircle size={48} color="green" />}
            </Box>
            
            <Typography variant="h6" gutterBottom>
              {submitState.message}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={submitState.progress} 
              sx={{ mt: 2, height: 8, borderRadius: 4 }}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {submitState.progress}% complete
            </Typography>

            {receiptPhoto && submitState.stage === 'uploading' && (
              <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                {receiptPhoto.name}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Backdrop>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert 
          onClose={() => setShowSuccessSnackbar(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            fontSize: '1.1rem',
            '& .MuiAlert-icon': { fontSize: '1.5rem' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle size={24} />
            Expense submitted successfully! Form has been reset for your next expense.
          </Box>
        </Alert>
      </Snackbar>

      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto', my: 4 }}>
        <Typography variant="h5" gutterBottom>
          Submit Expense
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Box>
              <DesktopDatePicker
                label="Date"
                value={formData.date}
                onChange={handleDateChange}
                format="DD/MM/YYYY"
                disabled={submitState.isSubmitting}
              /> 
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Receipt Photo
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                mb: 2,
                opacity: submitState.isSubmitting ? 0.6 : 1
              }}>
                {previewUrl ? (
                  receiptPhoto?.type === 'application/pdf' ? (
                    <PDFPreview 
                      url={previewUrl}
                      onClear={() => {
                        setReceiptPhoto(null);
                        setPreviewUrl(null);
                      }}
                    />
                  ) : (
                    <Box sx={{ position: 'relative', width: '100%', maxWidth: 300 }}>
                      <img
                        src={previewUrl}
                        alt="Receipt preview"
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                      <IconButton
                        onClick={() => {
                          setReceiptPhoto(null);
                          setPreviewUrl(null);
                        }}
                        disabled={submitState.isSubmitting}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper'
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  )
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Upload size={48} strokeWidth={1} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      No receipt uploaded
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supported formats: Images and PDF
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Camera />}
                    onClick={handleCameraCapture}
                    disabled={!!receiptPhoto?.type?.includes('pdf') || submitState.isSubmitting}
                  >
                    Take Photo
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Upload />}
                    component="label"
                    disabled={submitState.isSubmitting}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                    />
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Amount and VAT Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                fullWidth
                label="Amount"
                type="number"
                required
                disabled={submitState.isSubmitting}
              />
          <Button 
  variant="outlined" 
  onClick={() => setIsCalcOpen(true)}
  disabled={submitState.isSubmitting}
  startIcon={<Calculator />}
>
  Calculate
</Button>
            </Box>

            {/* Business Name Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Business/Supplier
              </Typography>
              
            <Autocomplete
  freeSolo
  options={businessNames}
  value={formData.businessName}
  onInputChange={(event, newValue) => {
    setFormData({ ...formData, businessName: newValue });
  }}
  disabled={submitState.isSubmitting}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Business/Supplier Name"
      required
      fullWidth
    />
  )}
/>
            </Box>

            {/* Activity Select Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Activity Select
              </Typography>
              
              <Autocomplete
                fullWidth
                options={activityOptions}
                getOptionLabel={(option) => option.label}
                value={activityOptions.find(option => option.value === formData.activityId) || null}
                onChange={(event, newValue) => {
                  setFormData(prevState => ({
                    ...prevState,
                    activityId: newValue?.value || ''
                  }));
                }}
                disabled={submitState.isSubmitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Activity"
                    required
                    error={!formData.activityId}
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Box>

            {/* Staff Member Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Staff Member
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Staff Member</InputLabel>
                <Select
                  name="staffId"
                  value={formData.staffId}
                  onChange={handleInputChange}
                  label="Staff Member"
                  required
                  disabled={submitState.isSubmitting}
                >
                  {staff.map((member) => (
                    <MenuItem key={member._id} value={member._id}>
                      {member.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* IMC Code Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                IMC Code
              </Typography>
              
<Autocomplete
  options={imcCodes}
  value={imcCodes.find(option => option.value === formData.imcCode) || null}
  onChange={(event, newValue) => {
  setFormData({ 
    ...formData, 
    imcCode: newValue?.value || '',
    imcCodeMondayId: newValue?.mondayId || ''
  });
}}
  getOptionLabel={(option) => option.label}
  disabled={submitState.isSubmitting}
  renderInput={(params) => (
    <TextField
      {...params}
      label="IMC Code"
      required
      fullWidth
    />
  )}
/>
            </Box>

            {/* Description Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              
              <TextField
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                label="Description"
                multiline
                rows={3}
                required
                disabled={submitState.isSubmitting}
              />
            </Box>

            {/* Payment Method Section */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Method
              </Typography>
              
            <Autocomplete
  options={paymentMethods}
  value={paymentMethods.find(option => option.mondayId === formData.paymentMethod) || null}
  onChange={(event, newValue) => {
setFormData({ ...formData, paymentMethod: newValue?.mondayId || '' });
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Payment Method"
      margin="normal"
      fullWidth
      required
    />
  )}
/>
            </Box>

            {submitState.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitState.error}
              </Alert>
            )}

            <Box sx={{ position: 'relative' }}>
              <Button
                type="submit"
                variant="contained"
                color={buttonConfig.color}
                size="large"
                fullWidth
                disabled={buttonConfig.disabled}
                startIcon={buttonConfig.icon}
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  ...(submitState.stage === 'success' && {
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' }
                  }),
                  ...(submitState.stage === 'error' && {
                    bgcolor: 'error.main',
                    '&:hover': { bgcolor: 'error.dark' }
                  })
                }}
              >
                {buttonConfig.text}
              </Button>
              
              {submitState.isSubmitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: `${submitState.progress}%`,
                    height: 4,
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    transition: 'width 0.3s ease',
                    borderRadius: '0 0 4px 4px'
                  }}
                />
              )}
            </Box>

            {submitState.isSubmitting && (
              <Fade in={true}>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {submitState.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {submitState.progress}% complete • Please don't close this page
                  </Typography>
                </Box>
              </Fade>
            )}

            {submitState.isSubmitting && receiptPhoto && (
              <Fade in={true}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Uploading Receipt:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {receiptPhoto.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(receiptPhoto.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              </Fade>
            )}
          </Stack>
        </form>

        {/* Calculator Modal */}
        <Dialog 
          open={isCalcOpen} 
          onClose={() => setIsCalcOpen(false)} 
          maxWidth="xs" 
          fullWidth
        >
          <DialogTitle>ShowRunnr Calc-O-Matic</DialogTitle>
          <DialogContent>
            <ShowRunnrCalc 
  initialValue={formData.amount ? parseFloat(formData.amount) : 0} 
  initialMode="calculator"
  onSubmit={handleCalcSubmit} 
  onClose={() => setIsCalcOpen(false)} 
/>
          </DialogContent>
        </Dialog>
      </Paper>
    </LocalizationProvider>
  );
};

export default ExpenseForm;